import { useMemo } from 'react';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { isMobile } from 'react-device-detect';
import { ThLoading } from '@components/elements';
import { useGoogleMaps } from '@state/googleMapsProvider';

interface MarkerLabel {
  text: string;
  className?: string;
}

export interface Marker {
  lat: number;
  lng: number;
  label?: MarkerLabel;
}

interface GoogleMapsProps {
  latitude: number;
  longitude: number;
  markers: Marker[];
  widthSize?: string;
  heightSize?: string;
  initialZoom?: number;
}

function GoogleMapDisplay({
  latitude,
  longitude,
  markers,
  initialZoom = 15,
  widthSize,
  heightSize,
}: GoogleMapsProps) {
  // Compute bounds only for 2+ markers
  const defaultBounds = useMemo(() => {
    if (markers.length > 1 && window.google?.maps) {
      const b = new window.google.maps.LatLngBounds();
      markers.forEach(({ lat, lng }) => b.extend({ lat, lng }));
      return {
        ...b.toJSON(),
        padding: { top: 60, right: 30, bottom: 30, left: 30 },
      };
    }
    return undefined;
  }, [markers]);

  // For a single marker, use center/zoom
  const center =
    markers.length === 1
      ? { lat: markers[0].lat, lng: markers[0].lng }
      : { lat: latitude, lng: longitude };
  const zoom = markers.length === 1 ? initialZoom : 10;

  return (
    <Map
      {...(defaultBounds
        ? { defaultBounds }
        : { defaultCenter: center, defaultZoom: zoom })}
      style={{
        width: isMobile ? (widthSize ?? '130px') : (widthSize ?? '335px'),
        height: heightSize ?? '125px',
      }}
      mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID}
    >
      {markers.map((marker, idx) => (
        <AdvancedMarker
          key={`marker-${marker.lat}-${marker.lng}-${marker.label?.text ?? idx}`}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={marker.label?.text}
        >
          {/* Custom SVG marker with label above pin */}
          <svg
            width="40"
            height="70"
            viewBox="0 0 40 70"
            style={{
              overflow: 'visible',
              pointerEvents: 'none',
            }}
            aria-label={marker.label?.text}
          >
            {marker.label?.text && (
              <text
                x="12"
                y="12"
                textAnchor="middle"
                fontSize="14"
                fontWeight="bold"
                fill="#333"
                stroke="#fff"
                paintOrder="stroke"
                style={{
                  dominantBaseline: 'middle',
                  pointerEvents: 'none',
                  fontFamily: 'inherit',
                }}
              >
                {marker.label.text}
              </text>
            )}
            {/* Google Maps style pin (copied from google maps), using the Pin component from the library has issues displaying the label */}
            <g transform="translate(0, 20)" fill="none" fillRule="evenodd">
              <path
                className="RIFJN-maps-pin-view-border"
                d="M13 0C5.8175 0 0 5.77328 0 12.9181C0 20.5733 5.59 23.444 9.55499 30.0784C12.09 34.3207 11.3425 37 13 37C14.7225 37 13.975 34.2569 16.445 30.1422C20.085 23.8586 26 20.6052 26 12.9181C26 5.77328 20.1825 0 13 0Z"
                fill="#C5221F"
              />
              <path
                className="RIFvHW-maps-pin-view-background"
                fill="#EA4335"
                d="M13.0167 35C12.7836 35 12.7171 34.9346 12.3176 33.725C11.9848 32.6789 11.4854 31.0769 10.1873 29.1154C8.92233 27.1866 7.59085 25.6173 6.32594 24.1135C3.36339 20.5174 1 17.7057 1 12.6385C1.03329 6.19808 6.39251 1 13.0167 1C19.6408 1 25 6.23078 25 12.6385C25 17.7057 22.6699 20.55 19.6741 24.1462C18.4425 25.65 17.1443 27.2193 15.8793 29.1154C14.6144 31.0442 14.0818 32.6135 13.749 33.6596C13.3495 34.9346 13.2497 35 13.0167 35Z"
              />
              <path
                className="KWCFZI-maps-pin-view-default-glyph"
                d="M13 18C15.7614 18 18 15.7614 18 13C18 10.2386 15.7614 8 13 8C10.2386 8 8 10.2386 8 13C8 15.7614 10.2386 18 13 18Z"
                fill="#B31412"
              />
              <image
                x="50%"
                y="50%"
                preserveAspectRatio="xMidYMid meet"
                width="15px"
                height="15px"
                href=""
              />
              <text x="50%" y="50%" textAnchor="middle" fill="#B31412" />
            </g>
          </svg>
        </AdvancedMarker>
      ))}
    </Map>
  );
}

export default function GoogleMapComponent(props: GoogleMapsProps) {
  const { isLoaded, loadError } = useGoogleMaps();

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <ThLoading />;
  }

  return <GoogleMapDisplay {...props} />;
}
